import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchPostProcessingService {
  private composer: EffectComposer | undefined;
  private outlinePass: OutlinePass | undefined;

  constructor() { }

  public init(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    width: number,
    height: number
  ) {
    this.composer = new EffectComposer(renderer);
    this.composer.setPixelRatio(renderer.getPixelRatio()); // Critical for High DPI
    this.composer.setSize(width, height);

    // 1. Render Pass (Base Scene)
    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // 2. Outline Pass (Selection Highlight)
    this.outlinePass = new OutlinePass(
      new THREE.Vector2(width, height),
      scene,
      camera
    );

    // Configuration for "Blue Glow" style (Enhanced for visibility over textures)
    this.outlinePass.edgeStrength = 5; // Stronger glow
    this.outlinePass.edgeGlow = 0.5;
    this.outlinePass.edgeThickness = 2; // Thicker lines
    this.outlinePass.pulsePeriod = 0;
    this.outlinePass.visibleEdgeColor.setHex(0x00aaff); // Light Blue
    this.outlinePass.hiddenEdgeColor.setHex(0x00aaff); // Same blue for hidden edges (Always on top effect)
    this.outlinePass.overlayMaterial.blending = THREE.CustomBlending; // Ensure proper blending if needed, but default is usually fine.
    // Actually, simply ensuring hidden color is set should work.
    // Let's also ensure depth test is false for the overlay? No, OutlinePass handles this via existing material.
    // The key is likely the Pixel Ratio fixed above.

    this.composer.addPass(this.outlinePass);

    // 3. Output Pass (Color Correction / Tone Mapping)
    const outputPass = new OutputPass();
    this.composer.addPass(outputPass);
  }

  public reset() {
    this.composer.dispose();
    this.outlinePass.dispose();
  }

  public resize(width: number, height: number) {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
    if (this.outlinePass) {
      this.outlinePass.resolution.set(width, height);
    }
  }

  public updateSelection(selectedObjects: THREE.Object3D[]) {
    if (this.outlinePass) {
      this.outlinePass.selectedObjects = selectedObjects;
    }
  }

  public render() {
    if (this.composer) {
      this.composer.render();
    }
  }

  public setOutlineColor(colorHex: number) {
    if (this.outlinePass) {
      this.outlinePass.visibleEdgeColor.setHex(colorHex);
    }
  }
}
